import { getApiChara } from "../../../util/getApi";
import { getAuthToken } from "../../../util/auth";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useContext } from "react";
import BillReceipt, { printBillReceipt } from "./BillReceipt";
import Loader from "../../UI/Loader";
import { toast } from "react-toastify";
import { Table, Button, Modal, Spinner } from "react-bootstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import "react-toastify/dist/ReactToastify.css";
import AuthContext from "../../../store/auth-context";
import { FaSearch, FaPlus, FaPrint, FaFileExport, FaSave, FaCheck, FaWhatsapp, FaTimes } from 'react-icons/fa';
import { parseHeader, drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { themeColors } from "../../../util/themeColors";
import { shareBillViaWhatsApp } from "./BillStatementReceipt";
import { useTranslation } from "react-i18next";

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};


const NewBill = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const hospitalData = authCtx.hospitalData;

  const [isLoading, setIsLoading] = useState(false);
  const expenseInput = useRef();
  const amountInput = useRef();
  const quantityInput = useRef();
  const discountInput = useRef();
  const [createdBill, setCreatedBill] = useState([]);
  const [patientData, setPatientData] = useState([]);
  const [searchPara, setSearchPara] = useState("");
  const [selectedPatient, setSelectedPatient] = useState({});
  const [totalBill, setTotalBill] = useState(0);
  // const [printAfterSave, setPrintAfterSave] = useState(false);
  const [printAfterSave, setPrintAfterSave] = useState(
    authCtx.hospitalData.OPDPrintBill === true || authCtx.hospitalData.OPDPrintBill === "true"
  );
  const [amountReceived, setAmountReceived] = useState("");
  const [outstandingAmount, setOutstandingAmount] = useState("");
  const [patientOutstanding, setPatientOutstanding] = useState(null);
  const [doPrint, setDoPrint] = useState(false);
  const [billingDate, setBillingDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idCounter = useRef(1);
  const [paymentType, setPaymentType] = useState("cash");
  const [txnNo, setTxnNo] = useState("");
  const [note, setNote] = useState("");
  // Export states
  const [exportFormat, setExportFormat] = useState("a4");
  const [isExporting, setIsExporting] = useState(false);
  const [charges, setCharges] = useState([]);
  const [savedBillId, setSavedBillId] = useState(null);

  // Set default quantity to 1 and fetch all charges on mount
  useEffect(() => {
    if (quantityInput.current) {
      quantityInput.current.value = "1";
    }
    fetchAllCharges();
  }, []);

  const fetchAllCharges = async () => {
    const token = getAuthToken();
    const apiChar = getApiChara();
    try {
      const response = await fetch(`${apiChar}/charge-master`, {
        headers: { Authorization: "Bearer " + token },
      });
      if (response.ok) {
        const data = await response.json();
        setCharges(data || []);
      }
    } catch (error) {
      console.error("Error fetching charges:", error);
    }
  };

  // Handle search input
  const getSerachParaHandler = (e) => setSearchPara(e.target.value);

  // Search patient
  const onSearchPatientHandler = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }
    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl =
        `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=` +
        searchPara;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Could Not fetch details");
      const data = await response.json();
      setPatientData(data);
      setCreatedBill([]);
    } catch (err) {
      toast.error(t("Failed to fetch patient data"));
    } finally {
      setIsLoading(false);
    }
  };

  const [showChargeDropdown, setShowChargeDropdown] = useState(false);
  const [expenseSearch, setExpenseSearch] = useState("");

  const filteredCharges = expenseSearch
    ? charges.filter(c => c.chargeName.toLowerCase().includes(expenseSearch.toLowerCase()))
    : charges;

  const onSearchChargeHandler = (term) => {
    setExpenseSearch(term);
    setShowChargeDropdown(true);
  };

  const handleChargeSelect = (charge) => {
    expenseInput.current.value = charge.chargeName;
    setExpenseSearch(charge.chargeName);
    amountInput.current.value = charge.amount;
    setShowChargeDropdown(false);
  };

  // Add bill item
  const onSubmitBillHandler = () => {
    const expenseName = expenseInput.current.value;
    const amount = parseFloat(amountInput.current.value || 0);
    const quantity = parseFloat(quantityInput.current.value || 1);
    const discount = parseFloat(discountInput.current.value || 0); // now percentage

    if (!expenseName && !amount && !selectedPatient?.patientId) {
      toast.warning(
        t("Please select a patient and enter expense name, amount and quantity.")
      );
      return;
    } else if (!expenseName) {
      toast.warning(t("Please enter expense name."));
      return;
    } else if (amountInput.current.value === "" || isNaN(amount)) {
      toast.warning(t("Please enter amount."));
      return;
    } else if (quantityInput.current.value === "" || isNaN(quantity) || quantity <= 0) {
      toast.warning(t("Please enter quantity greater than 0."));
      return;
    } else if (!selectedPatient?.patientId) {
      toast.warning(t("Please select a patient."));
      return;
    }

    if (createdBill.some((bill) => bill.expenseName === expenseName)) {
      toast.warning(t("Expense name already exists."));
      return;
    }

    const actualAmount = amount;

    const netAmount = parseFloat(actualAmount * quantity * (1 - discount / 100));

    const billingData = {
      id: idCounter.current++,
      expenseName,
      actualAmount: actualAmount.toFixed(2),
      netAmount: netAmount.toFixed(2),
      quantity,
      totalAmount: (actualAmount * quantity).toFixed(2),
      discount,
      date: billingDate,
    };

    setCreatedBill((prev) => [...prev, billingData]);

    expenseInput.current.value = "";
    amountInput.current.value = "";
    quantityInput.current.value = "1";
    discountInput.current.value = "";
  };


  // Remove bill item
  const removeBillItem = (indexToRemove) => {
    setCreatedBill((prevBill) => {
      const updatedBill = prevBill.filter((_, index) => index !== indexToRemove);
      const refreshedBill = updatedBill.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      idCounter.current = refreshedBill.length + 1;
      return refreshedBill;
    });
  };

  // Update total whenever createdBill changes
  useEffect(() => {
    const total = createdBill.reduce(
      (sum, item) => sum + parseFloat(item.netAmount || 0),
      0
    );
    setTotalBill(parseFloat(total.toFixed(2)));
    setAmountReceived(parseFloat(total.toFixed(2)));
  }, [createdBill]);

  // Update outstanding whenever totalBill or amountReceived changes
  // useEffect(() => {
  //   const received = parseFloat(amountReceived) || 0;
  //   const outstanding = totalBill - received;
  //   setOutstandingAmount(outstanding >= 0 ? outstanding.toFixed(2) : "0");
  // }, [totalBill, amountReceived]);

  useEffect(() => {
    const received = parseFloat(amountReceived) || 0;
    const outstanding = totalBill - received;
    setOutstandingAmount(outstanding.toFixed(2)); // ✅ keep negative values too
  }, [totalBill, amountReceived]);
  // Close modal
  const onDataCloseHandler = () => {
    setPatientData([]);
    navigate(".");
  };

  // Select patient
  const getSelectedPatientFromList = async (patient) => {
    setSelectedPatient(patient);

    if (!patient?.patientId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/billings/getOutstandingAmountByPatientId?patientId=${patient.patientId}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch outstanding amount");

      const amount = await response.json();
      setPatientOutstanding(amount);
    } catch (err) {
      console.error("Error fetching outstanding:", err);
      setPatientOutstanding(0);
    } finally {
      // ✅ Close modal after selecting patient
      setPatientData([]);
    }
  };

  // const onAmountReceivedChange = (e) => {
  //   const value = e.target.value;

  //   if (value === "") {
  //     setAmountReceived("");
  //     setOutstandingAmount(totalBill);
  //     return;
  //   }

  //   const num = parseFloat(value) || 0;
  //   setAmountReceived(num);

  //   let outstanding = totalBill - num;
  //   outstanding = parseFloat(outstanding.toFixed(2)); // ✅ round properly
  //   setOutstandingAmount(outstanding >= 0 ? outstanding : 0);
  // };
  // Save bill
  const onAmountReceivedChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setAmountReceived("");
      setOutstandingAmount(totalBill.toFixed(2));
      return;
    }

    const num = parseFloat(value) || 0;
    setAmountReceived(num);

    let outstanding = totalBill - num;
    outstanding = parseFloat(outstanding.toFixed(2));
    setOutstandingAmount(outstanding); // ✅ no clamping
  };

  const onSaveBillHandler = async () => {
    if (!selectedPatient?.patientId) {
      toast.error(t("Patient details are required before saving bill"));
      return;
    }
    if (!paymentType) {
      toast.warning(t("Please select a payment type before submitting"));
      return;
    }
    if (!amountReceived) { //|| parseFloat(amountReceived) <= 0
      toast.warning(t("Please enter the amount received before submitting"));
      return;
    }
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }
    setIsSubmitting(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/billings/createBilling`;

      const formattedBillingDate = `${billingDate.getFullYear()}-${String(
        billingDate.getMonth() + 1
      ).padStart(2, "0")}-${String(billingDate.getDate()).padStart(2, "0")} ${String(
        billingDate.getHours()
      ).padStart(2, "0")}:${String(billingDate.getMinutes()).padStart(2, "0")}:${String(
        billingDate.getSeconds()
      ).padStart(2, "0")}`;
      const payload = {
        id: 0,
        billReceivedById: {
          userId: authCtx.userId, // Use context
          role: authCtx.role, // Use context
          userName: authCtx.userName, // Use context
        },
        patientId: selectedPatient,
        billingDate: formattedBillingDate, // ✅ now includes time
        paymentType: paymentType || "",
        txnNo: txnNo || "",
        note: note,
        totalAmount: totalBill.toFixed(2),
        amountReceived: parseFloat(amountReceived).toFixed(2),
        outstandingAmount: parseFloat(outstandingAmount).toFixed(2),
        userName: authCtx.userName, // Use context
        patientName: `${selectedPatient?.firstName || ""} ${selectedPatient?.middleName || ""
          } ${selectedPatient?.lastName || ""}`.trim(),
        serviceItems: createdBill.map((bill) => ({
          serviceDescription: bill.expenseName,
          actualAmount: parseFloat(bill.actualAmount).toFixed(2),
          totalAmount: (bill.actualAmount * bill.quantity).toFixed(2), // string "200.00"
          netAmount: parseFloat(bill.netAmount).toFixed(2),       // string "216.69"
          quantity: bill.quantity,
          discount: bill.discount,
        })),
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        toast.error(t("Failed to save bill"));
        return;
      }

      const responseData = await response.json();
      const newBillId = responseData.id;
      setSavedBillId(newBillId);

      toast.success(t("Bill Saved successfully!"));

      if (printAfterSave) {
        setDoPrint(true);
      } else {
        resetForm();
      }
    } catch (error) {
      console.error("Error saving bill:", error);
      toast.error("Failed to save bill");
    } finally {
      setIsSubmitting(false); // ✅ stop submitting
    }
  };

  // Reset form
  const resetForm = () => {
    setCreatedBill([]);
    setTotalBill(0);
    setSelectedPatient({});
    idCounter.current = 1;
    setAmountReceived("");   // not 0
    setOutstandingAmount("");
    setPaymentType("cash");
    setSavedBillId(null);
    setSearchPara("");
    setExpenseSearch("");
    if (expenseInput.current) {
      expenseInput.current.value = "";
    }
  };

  // Handle printing after save
  useEffect(() => {
    if (doPrint) {
      printBillReceipt(
        selectedPatient,
        createdBill,
        totalBill,
        amountReceived,
        outstandingAmount,
        billingDate,
        paymentType,
        txnNo,
        hospitalData,
        savedBillId
      );
      setDoPrint(false);
      resetForm();
    }
  }, [doPrint]);

  // ---------- PDF EXPORT (Separate for A4 & A5) ----------

  const buildPDF = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 18;
    const startY = drawReportHeader(doc, hospitalData, "Bill Receipt", { margin: 0, top: 0, hideSeparator: true, isExport: true });

    currentY = startY + 5;

    doc.setTextColor(0, 0, 0);
    // 🔹 Patient Info
    doc.setFontSize(11);
    doc.text(
      `Name: ${selectedPatient?.firstName || ""} ${selectedPatient?.lastName || ""}`,
      14,
      currentY
    );
    const dateStr = new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    doc.text(`Date: ${dateStr}`, pageWidth - 75, currentY);

    currentY += 7;
    doc.text(`Bill No: ${savedBillId || "-"}`, 14, currentY);
    doc.text(`Mobile: ${selectedPatient?.mobile || "-"}`, pageWidth - 75, currentY);

    currentY += 7;
    doc.text(
      `Address: ${selectedPatient?.address ? selectedPatient.address : "-"}`,
      14,
      currentY
    );

    // 🔹 Bill Table
    currentY += 8;
    doc.autoTable({
      startY: currentY,
      head: [
        ["Sr. No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
      ],
      body: createdBill.map((bill, index) => [
        index + 1,
        bill.expenseName,
        bill.actualAmount,
        bill.quantity,
        bill.actualAmount * bill.quantity,
        bill.discount,
        bill.netAmount
      ]),
      foot: [
        ["", "", "", "", "", "Total", totalBill.toFixed(2)]
      ],
      theme: "grid",
      styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        halign: "center",
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      bodyStyles: { fillColor: [255, 255, 255] },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        halign: "right",
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      }
    });

    // 🔹 Summary
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.text(`Amount Received: ${amountReceived}`, 14, finalY);
    doc.text(`Outstanding Amount: ${outstandingAmount}`, pageWidth - 75, finalY);

    let nextY = finalY + 8;
    doc.text(`Payment Type: ${paymentType || "-"}`, 14, nextY);
    if (paymentType && paymentType.toLowerCase() !== "cash") {
      doc.text(`Txn No: ${txnNo || "-"}`, pageWidth - 75, nextY);
    }

    drawAuthorizedSignatory(doc, hospitalData);
  };

  const buildPDFA5 = (doc) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 15;
    const startY = drawReportHeader(doc, hospitalData, "Bill Receipt", { margin: 0, top: 0, hideSeparator: true, isExport: true });

    currentY = startY + 5;

    doc.setTextColor(0, 0, 0);
    // 🔹 Patient Info
    doc.setFontSize(10);
    doc.text(
      `Name: ${selectedPatient?.firstName || ""} ${selectedPatient?.lastName || ""}`,
      14,
      currentY
    );
    const dateStr = new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    doc.text(`Date: ${dateStr}`, pageWidth - 60, currentY);

    currentY += 6;
    doc.text(`Bill No: ${savedBillId || "-"}`, 14, currentY);
    doc.text(`Mobile: ${selectedPatient?.mobile || "-"}`, pageWidth - 60, currentY);

    currentY += 6;
    doc.text(
      `Address: ${selectedPatient?.address ? selectedPatient.address : "-"}`,
      14,
      currentY
    );

    // 🔹 Bill Table
    currentY += 8;
    doc.autoTable({
      startY: currentY,
      head: [
        ["Sr. No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
      ],
      body: createdBill.map((bill, index) => [
        index + 1,
        bill.expenseName,
        bill.actualAmount,
        bill.quantity,
        bill.actualAmount * bill.quantity,
        bill.discount,
        bill.netAmount
      ]),
      foot: [
        ["", "", "", "", "", "Total", totalBill.toFixed(2)]
      ],
      theme: "grid",
      styles: {
        fontSize: 7,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        halign: "center",
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      bodyStyles: { fillColor: [255, 255, 255] },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        halign: "right",
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      }
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.text(`Amount Received: ${amountReceived}`, 14, finalY);
    doc.text(`Outstanding Amount: ${outstandingAmount}`, pageWidth - 60, finalY);

    let nextY = finalY + 6;
    doc.text(`Payment Type: ${paymentType || "-"}`, 14, nextY);
    if (paymentType && paymentType.toLowerCase() !== "cash") {
      doc.text(`Txn No: ${txnNo || "-"}`, pageWidth - 60, nextY);
    }

    drawAuthorizedSignatory(doc, hospitalData);
  };


  const exportPDF_A4 = () => {
    setIsExporting(true);
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    buildPDF(doc);
    // Generate filename with patient name
    const patientName = `${selectedPatient?.firstName || ""}_${selectedPatient?.lastName || ""}`.trim();
    const safeName = patientName.replace(/\s+/g, "_") || "patient"; // avoid empty names
    doc.save(`${safeName}_bill.pdf`);
    setIsExporting(false);
  };

  const exportPDF_A5 = () => {
    setIsExporting(true);
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a5",
    });
    buildPDFA5(doc);
    // Generate filename with patient name
    const patientName = `${selectedPatient?.firstName || ""}_${selectedPatient?.lastName || ""}`.trim();
    const safeName = patientName.replace(/\s+/g, "_") || "patient"; // avoid empty names
    doc.save(`${safeName}_bill.pdf`);
    setIsExporting(false);
  };

  const exportPDF = () => {
    if (!createdBill.length) {
      toast.error("No bill to export");
      return;
    }
    exportFormat === "a4" ? exportPDF_A4() : exportPDF_A5();
  };

  const handleWhatsAppShare = async () => {
    if (!selectedPatient) {
      toast.error(t("Please select a patient"));
      return;
    }
    if (!createdBill.length) {
      toast.error(t("No bill to share"));
      return;
    }
    // Prepare service items for export
    const serviceItems = createdBill.map(item => ({
      serviceDescription: item.expenseName,
      actualAmount: item.actualAmount,
      quantity: item.quantity,
      discount: item.discount,
      netAmount: item.netAmount
    }));

    await shareBillViaWhatsApp(
      selectedPatient,
      serviceItems,
      totalBill,
      amountReceived,
      outstandingAmount,
      billingDate,
      paymentType,
      txnNo,
      hospitalData
    );
  };

  return (
    <>
      {isLoading && <Loader />}
      {/* ✅ Patient Selection Modal using Bootstrap */}
      <Modal
        show={patientData?.length > 0}
        onHide={onDataCloseHandler}
        centered
        size="lg"
        className="patientModel"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "var(--primary-color)",
            color: "white",
            border: "none",
          }}
        >
          <Modal.Title className="w-100 text-center text-white" style={{ color: "white" }}>
            {t("Select Patient")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>{t("Patient Id")}</th>
                <th>{t("Name")}</th>
                <th>{t("Mobile")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.map((p) => (
                <tr key={p.patientId}>
                  <td>{p.patientId}</td>
                  <td>
                    {`${p.firstName || ""} ${p.middleName ? p.middleName + " " : ""
                      }${p.lastName || ""}`.trim()}
                  </td>
                  <td>{p.mobile || "-"}</td>
                  <td>
                    <Button
                      className="btn-select-premium"
                      onClick={() => getSelectedPatientFromList(p)}
                    >
                      <FaCheck className="me-1" /> {t("Select")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
      <div className="container-fluid px-2 px-md-4 pt-2" style={{ overflowX: 'hidden' }}>
        <h5 className="textReports pb-3">{t("Create New Bill")}</h5>
        <div className="row mb-3 g-3" id="hideInPrint">
          {/* Search Patient */}
          <div className="col-lg-6 col-12">
            <form onSubmit={onSearchPatientHandler}>
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("Search Patient By Mobile Number")}
                  value={searchPara}
                  onChange={getSerachParaHandler}
                  required
                  style={{ border: "1px solid #dcdde1" }}
                />
                <button type="submit" className="btn-premium" style={{ borderRadius: '0 8px 8px 0' }}>
                  <FaSearch />
                </button>
              </div>
            </form>
          </div>
          {/* Billing Date */}
          <div className="col-lg-6 col-12 mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <label className="fw-bold small me-2 mb-0">{t("Billing Date")}:</label>
              <input
                type="date"
                className="form-control"
                style={{ maxWidth: "270px", border: "1px solid #dcdde1" }}
                value={billingDate instanceof Date ? billingDate.toISOString().split("T")[0] : ""}
                onChange={(e) => {
                  const [year, month, day] = e.target.value.split("-");
                  let newDate = new Date(billingDate); // keep current time
                  if (isNaN(newDate.getTime())) {
                    // fallback if billingDate is invalid
                    newDate = new Date();
                  }
                  newDate.setFullYear(+year, +month - 1, +day);
                  setBillingDate(newDate);
                }}
              />
            </div>
          </div>
        </div>



        {/* Expense Input Fields Row */}
        <div className="row g-2 px-1">
          {/* Expense Name with Searchable Dropdown */}
          <div className="col-lg-3 col-6 mb-1 position-relative">
            <label htmlFor="expenseName" className="fw-bold small">{t("Expense Name")}</label>
            <div className="input-group input-group-sm">
              <input
                type="text"
                id="expenseName"
                className="form-control"
                ref={expenseInput}
                autoComplete="off"
                placeholder={t("Select or Search...")}
                style={{ border: "1px solid #dcdde1" }}
                value={expenseSearch}
                onChange={(e) => onSearchChargeHandler(e.target.value)}
                onFocus={() => setShowChargeDropdown(true)}
              />
              {expenseSearch && (
                <span
                  className="input-group-text bg-white"
                  style={{ cursor: 'pointer', border: "1px solid #dcdde1", borderLeft: 'none', borderRight: 'none' }}
                  onClick={() => {
                    setExpenseSearch("");
                    if (expenseInput.current) expenseInput.current.value = "";
                    setShowChargeDropdown(false);
                  }}
                >
                  <FaTimes className="text-secondary" />
                </span>
              )}
              <span
                className="input-group-text bg-white"
                style={{ cursor: 'pointer', border: "1px solid #dcdde1", borderLeft: 'none' }}
                onClick={() => setShowChargeDropdown(!showChargeDropdown)}
              >
                <i className={`fa fa-caret-${showChargeDropdown ? 'up' : 'down'}`} />
              </span>
            </div>

            {showChargeDropdown && (
              <div
                className="position-absolute w-100 bg-white border rounded shadow-sm overflow-auto"
                style={{ zIndex: 1000, maxHeight: '200px', top: '100%' }}
              >
                {filteredCharges.length === 0 ? (
                  <div className="p-2 text-muted small">{t("No matches found")}</div>
                ) : (
                  filteredCharges.map((c, idx) => (
                    <div
                      key={idx}
                      className="p-2 border-bottom small"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      onClick={() => handleChargeSelect(c)}
                    >
                      {c.chargeName} ( <i className="fa fa-inr me-1"></i>{c.amount} )
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="col-lg-1 col-6 mb-1">
            <label htmlFor="quantity" className="fw-bold small">{t("Quantity")}</label>
            <input
              type="number"
              id="quantity"
              className="form-control"
              ref={quantityInput}
              min={1}
              defaultValue={1}
              style={{ border: "1px solid #dcdde1" }}
            />
          </div>

          {/* Amount */}
          <div className="col-lg-3 col-6 mb-1">
            <label htmlFor="amount" className="fw-bold small">{t("Actual Amount")}</label>
            <input
              type="number"
              id="amount"
              className="form-control"
              ref={amountInput}
              style={{ border: "1px solid #dcdde1" }}
            />
          </div>

          {/* Discount */}
          <div className="col-lg-2 col-6 mb-1">
            <label htmlFor="discount" className="fw-bold small">{t("Discount (%)")}</label>
            <input
              type="number"
              id="discount"
              className="form-control"
              ref={discountInput}
              style={{ border: "1px solid #dcdde1" }}
            />
          </div>

          {/* Add Button */}
          <div className="col-lg-3 col-12 mb-1 d-flex align-items-end">
            <button
              onClick={onSubmitBillHandler}
              className="btn-premium w-100"
            >
              <FaPlus className="me-2" /> {t("Add")}
            </button>
          </div>
        </div>


        <hr />

        {/* Patient Info */}
        {selectedPatient?.patientId && (
          <div className="row mb-3">
            {/* Patient ID */}
            <div className="col-md-3">
              <strong>{t("Patient Id")}:</strong> {selectedPatient.patientId}
            </div>

            {/* Full Name */}
            <div className="col-md-5">
              <strong>{t("Name")}:</strong>{" "}
              {[selectedPatient.firstName, selectedPatient.middleName, selectedPatient.lastName]
                .filter(Boolean) // removes null, undefined, empty strings
                .join(" ")}
            </div>

            {/* Outstanding Amount */}
            <div className="col-md-4">

              <strong>{t("Outstanding Amount")}:</strong>{" "}
              {patientOutstanding !== null && patientOutstanding !== undefined
                ? patientOutstanding
                : "--"} <i className="fa fa-inr me-1"></i>
            </div>
          </div>
        )}

        {/* Bill Table */}
        <div className="printData boxes table-responsive mt-2">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>{t("Sr No")}</th>
                <th>{t("Date")}</th>
                <th>{t("Expense Name")}</th>
                <th>{t("Actual Amount")}</th>
                <th>{t("Quantity")}</th>
                <th>{t("Total Amount")}</th>
                <th>{t("Discount (%)")}</th>
                <th>{t("Net Amount")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {createdBill.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center">
                    {t("No Record found")}
                  </td>
                </tr>
              )}
              {createdBill.map((bill, index) => (
                <tr key={bill.id}>
                  <td>{bill.id}</td>
                  <td>{bill.date instanceof Date ? bill.date.toLocaleString() : bill.date}</td>
                  <td>{bill.expenseName}</td>
                  <td>{bill.actualAmount}</td>
                  <td>{bill.quantity}</td>
                  <td>{bill.actualAmount * bill.quantity}</td>
                  <td>{bill.discount}%</td> {/* show % */}
                  <td>{bill.netAmount}</td>
                  <td>
                    <button className="btn btn-link" onClick={() => removeBillItem(index)}>X</button>
                  </td>
                </tr>
              ))}
              {createdBill.length > 0 && (
                <tr>
                  <td colSpan="6" className="text-center"></td>
                  <td className="text-center"><strong>{t("Total")}</strong></td>
                  <td className="text-center">{totalBill.toFixed(2)}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        {/* Amount Received / Outstanding */}

        {createdBill.length > 0 && (
          <div>
            <div className="row mt-3 g-2">
              <div className="col-md-2 col-6">
                <label className="fw-bold small">{t("Amount Received")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={amountReceived}
                  onChange={onAmountReceivedChange}
                  max={totalBill}
                  style={{ border: "1px solid #dcdde1" }}
                />
              </div>
              <div className="col-md-2 col-6">
                <label className="fw-bold small">{t("Outstanding Amount")}</label>
                <input
                  type="number"
                  className="form-control"
                  value={outstandingAmount}
                  readOnly
                  style={{ border: "1px solid #dcdde1" }}
                />
              </div>
              <div className="col-md-2 col-6">
                <label className="fw-bold small">{t("Payment Type")}</label>
                <select
                  className="form-select themed-select"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  style={{ border: "1px solid #dcdde1" }}
                >
                  <option value="">-- {t("Select")} --</option>
                  <option value="cash">{t("Cash")}</option>
                  <option value="card">{t("Card")}</option>
                  <option value="upi">{t("UPI")}</option>
                </select>
              </div>
              {paymentType && paymentType !== "cash" ? (
                <div className="col-md-2 col-6">
                  <label className="fw-bold small">{t("Txn No")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={txnNo}
                    onChange={(e) => setTxnNo(e.target.value)}
                    placeholder={t("Enter transaction no.")}
                    style={{ border: "1px solid #dcdde1" }}
                  />
                </div>
              ) : null}
              <div className={`${paymentType && paymentType !== "cash" ? "col-md-4" : "col-md-6"} col-12`}>
                <label className="fw-bold small">{t("Note")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("Enter Note")}
                  style={{ border: "1px solid #dcdde1" }}
                />
              </div>
            </div>
          </div>
        )}


        {/* Print / Export / Submit */}
        {createdBill.length > 0 && (
          <div id="options" className="mt-4">
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
              {/* {selectedPatient?.patientId ? (
                <BillReceipt
                  patientData={selectedPatient}
                  createdBill={createdBill}
                  totalBillAmount={calculateTotalBill()}
                  discount={+discountInput.current?.value || 0}
                  amountReceived={+amountReceived || 0}
                  paymentType={paymentType}
                >
                  <button
                    className="btn btn-outline-primary-premium"
                  >
                    <FaPrint className="me-2" /> {t("Print")}
                  </button>
                </BillReceipt>
              )} */}

              {/* Export Dropdown */}
              {/* <div className="d-flex align-items-center gap-2">
                <select
                  className="form-select themed-select"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  style={{ width: '80px' }}
                >
                  <option value="a4">A4</option>
                  <option value="a5">A5</option>
                </select>
                <button
                  onClick={exportPDF}
                  className="btn btn-export-premium"
                  disabled={isExporting}
                >
                  <FaFileExport className="me-2" /> {isExporting ? t("EXPORTING...") : t("Export")}
                </button>

                <button
                  onClick={handleWhatsAppShare}
                  className="btn-whatsapp-premium"
                >
                  <FaWhatsapp className="me-2" /> {t("WhatsApp")}
                </button>
              </div> */}

              {/* Save + Print After Save */}
              <div className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  id="printAfterSave"
                  checked={printAfterSave}
                  onChange={(e) => setPrintAfterSave(e.target.checked)}
                />
                <label htmlFor="printAfterSave" className="mb-0 fw-bold small">
                  {t("Print")}
                </label>
                <button
                  onClick={onSaveBillHandler}
                  disabled={createdBill.length === 0 || isSubmitting || savedBillId !== null}
                  className="btn-premium d-flex align-items-center"
                >
                  {isSubmitting ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    <FaSave className="me-2" />
                  )}
                  {savedBillId !== null ? t("Submitted") : isSubmitting ? t("Submitting...") : t("Submit")}
                </button>
                {/* {savedBillId && (
                  <button onClick={resetForm} className="btn-secondary-premium">
                    {t("New Bill")}
                  </button>
                )} */}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NewBill;
